import { useApolloClient } from '@apollo/client'
import {
  SignInMutationVariables,
  SignUpMutationVariables,
  SocialAuthMutationVariables,
  useSignInMutation,
  useSignUpMutation,
  useSocialAuthMutation,
} from '@graphql/mutations/__generated__/auth.generated'
import { ResetPasswordMutationVariables, useResetPasswordMutation } from '@graphql/mutations/__generated__/me.generated'
import { ContactMutationVariables, useContactMutation } from '@graphql/mutations/__generated__/site.generated'
import React, { useMemo } from 'react'
import Loader from '@components/Loader'
import { useHistory } from 'react-router'
import { QueryParamConfig, StringParam, useQueryParams } from 'use-query-params'
import { googlePushLogin, googlePushSignUp } from '../utils/dataLayer'
import ResponsiveDialog from '@components/ResponsiveDialog'
import { useTranslation } from 'react-i18next'

// Lazy-loaded: these dialogs (and SignInForm's Google/Facebook OAuth SDKs) are only needed
// once a user actually opens one, but GlobalDialogForms itself renders unconditionally on
// every route, so eager imports here would put them in every page's initial JS execution.
const PasswordResetForm = React.lazy(() => import('./Forms/AccountForms/PasswordResetForm'))
const SignInForm = React.lazy(() => import('./Forms/AccountForms/SignInForm/SignInForm'))
const SignUpForm = React.lazy(() => import('./Forms/AccountForms/SignUpForm/SignUpForm'))
const ContactForm = React.lazy(() => import('./Forms/ContactForm'))

export type TGlobalDialogForm = 'signIn' | 'signUp' | 'forgotPassword' | 'contactUs'

export const GlobalDialogFormsParams = {
  global_dialog_form: StringParam as QueryParamConfig<TGlobalDialogForm | null>,
  redirect: StringParam,
}

const GlobalDialogForms: React.FC = ({}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [params, setParams] = useQueryParams(GlobalDialogFormsParams)
  const client = useApolloClient()
  const [signIn] = useSignInMutation()
  const [socialAuth] = useSocialAuthMutation()
  const [resetPassword] = useResetPasswordMutation()
  const [signUp] = useSignUpMutation()
  const [contact] = useContactMutation()
  const [formErrors, setFormErrors] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)

  const setDialogAccountForm = (form: TGlobalDialogForm | null) => {
    setParams({ global_dialog_form: form || undefined }, 'replaceIn')
  }

  const submitData = async (
    func: Function,
    variables: SignInMutationVariables | ResetPasswordMutationVariables | SignUpMutationVariables
  ) => {
    let error = null
    try {
      const response = await func({ variables })
      const errors = (response.data.data && response.data.data.errors) || []
      const statusOk = errors.length === 0
      if (statusOk) {
        await client.refetchQueries({
          include: 'active',
        })
        // await client.resetStore()
      } else {
        error = errors
        setFormErrors(errors)
      }
    } catch (err) {
      console.log(err)
      error = err
    }
    return error
  }

  const onSubmitSignIn = async (values: SignInMutationVariables) => {
    setIsLoading(true)
    const error = await submitData(signIn, values)
    setIsLoading(false)
    if (!error) {
      googlePushLogin('Site')
      setDialogAccountForm(null)
      if (params.redirect) {
        history.push(params.redirect)
      }
    }
  }

  const onSubmitResetPassword = async (values: ResetPasswordMutationVariables) => {
    setIsLoading(true)
    await submitData(resetPassword, values)
    setDialogAccountForm(null)
    setIsLoading(false)
  }
  const onSubmitSignUp = async (values: SignUpMutationVariables) => {
    setIsLoading(true)
    const error = await submitData(signUp, values)
    setIsLoading(false)
    if (!error) {
      googlePushSignUp('Site')
      setDialogAccountForm(null)
      if (params.redirect) {
        history.push(params.redirect)
      }
    }
  }
  const onSubmitSocialSignIn = async (variables: SocialAuthMutationVariables) => {
    setIsLoading(true)
    try {
      await socialAuth({ variables })
      googlePushLogin(variables.provider)
      // await client.resetStore()
      await client.refetchQueries({
        include: 'active',
      })
      setIsLoading(false)
      setDialogAccountForm(null)
      if (params.redirect) {
        history.push(params.redirect)
      }
    } catch (e) {
      setFormErrors([{ messages: [e.message] }])
      setIsLoading(false)
    }
  }
  const onSubmitContactUs = async (values: ContactMutationVariables['input']) => {
    setIsLoading(true)
    try {
      await contact({ variables: { input: values } })
    } catch (err) {
      console.log(err)
    }
    setDialogAccountForm(null)
    setIsLoading(false)
  }
  const handleCloseModal = () => {
    setDialogAccountForm(null)
    setParams({ redirect: undefined }, 'replaceIn')
  }

  const setIsOpenDialog = (val: boolean) => {
    setDialogAccountForm(null)
    setParams({ redirect: undefined }, 'replaceIn')
  }

  const title = useMemo<string>(() => {
    if (params.global_dialog_form === 'contactUs') {
      return t('title.contactUs', 'Contact us')
    } else if (params.global_dialog_form === 'signIn') {
      return t('signIn', 'Sign in')
    } else if (params.global_dialog_form === 'forgotPassword') {
      return t('retrievePassword', 'Retrieve password')
    } else if (params.global_dialog_form === 'signUp') {
      return t('signUp', 'Sign up')
    }
    return ''
  }, [params.global_dialog_form])

  return (
    <ResponsiveDialog isOpenDialog={!!params.global_dialog_form} setIsOpenDialog={setIsOpenDialog} title={title}>
      <Loader className='spinner' type='ThreeDots' color='#333' visible={isLoading} height={100} width={100} />
      <React.Suspense
        fallback={<Loader className='spinner' type='ThreeDots' color='#333' visible height={100} width={100} />}
      >
        {params.global_dialog_form === 'signIn' && (
          <SignInForm
            onSubmitSignIn={onSubmitSignIn}
            onForgotPassword={() => setDialogAccountForm('forgotPassword')}
            onSubmitSocialSignIn={onSubmitSocialSignIn}
            formErrors={formErrors}
            handleCloseModal={handleCloseModal}
            handleSignUp={() => setDialogAccountForm('signUp')}
            variant='dialog'
          />
        )}
        {params.global_dialog_form === 'forgotPassword' && (
          <PasswordResetForm
            onSubmit={onSubmitResetPassword}
            formErrors={formErrors}
            variant='dialog'
            handleCloseModal={handleCloseModal}
            handleSignIn={() => setDialogAccountForm('signIn')}
          />
        )}
        {params.global_dialog_form === 'signUp' && (
          <SignUpForm
            onSubmit={onSubmitSignUp}
            formErrors={formErrors}
            handleCloseModal={handleCloseModal}
            onSubmitSocialSignIn={onSubmitSocialSignIn}
            handleSignIn={() => setDialogAccountForm('signIn')}
            variant='dialog'
          />
        )}
        {params.global_dialog_form === 'contactUs' && (
          <ContactForm
            onSubmit={onSubmitContactUs}
            formErrors={formErrors}
            handleCloseDialog={handleCloseModal}
            loading={isLoading}
            variant='dialog'
          />
        )}
      </React.Suspense>
    </ResponsiveDialog>
  )
}

export default GlobalDialogForms
